import { marked } from 'marked';
import hljs from 'highlight.js/lib/core';
import javascriptHl from 'highlight.js/lib/languages/javascript';
import typescriptHl from 'highlight.js/lib/languages/typescript';
import pythonHl from 'highlight.js/lib/languages/python';
import type { ConversationTurn } from '../pipeline/types';
import { AVAILABLE_MODELS } from '../api/pricing';
import { AGENT_PERSONAS } from '../agent/personas';
import { getTaskTemplates } from '../db/settings-repo';
import { TEMPLATES_CHANGED_EVENT } from './events';
import { showContextTreesOverview } from './contexttrees-overview';

hljs.registerLanguage('javascript', javascriptHl);
hljs.registerLanguage('typescript', typescriptHl);
hljs.registerLanguage('python', pythonHl);

export interface ConversationListItem {
  id: string;
  title: string;
  updatedAt: number;
}

export interface ChatCallbacks {
  onSend: (message: string, optimized: boolean) => void;
  /** Called when the user taps "Apply" on a code block — this app's interim apply mechanism until C1/C2 ship. */
  onApplyCodeBlock: (code: string) => void;
  onNewConversation: () => void;
  onSelectConversation: (id: string) => void;
  onModelChange: (model: string) => void;
  /** Benchmark mode: runs the optimized pipeline (capped at maxTokens) and the unoptimized baseline in parallel for comparison. */
  onRunComparison: (message: string, maxTokens: number) => void;
  /** Opens a browser of previously-saved benchmark comparison runs. */
  onShowBenchmarkHistory: () => void;
  /** Agent mode: runs an agentic tool-use loop (read/write/list/search files) with the given persona. */
  onRunAgent: (message: string, personaId: string) => void;
}

export class ChatPane {
  private messagesEl: HTMLElement;
  private inputEl!: HTMLTextAreaElement;
  private callbacks: ChatCallbacks;
  private streamingEl: HTMLElement | null = null;
  private streamingText = '';
  private conversationSelect!: HTMLSelectElement;
  private modelSelect!: HTMLSelectElement;
  private comparisonToggle!: HTMLInputElement;
  private comparisonMaxTokensInput!: HTMLInputElement;
  private agentToggle!: HTMLInputElement;
  private agentPersonaSelect!: HTMLSelectElement;
  private unoptimizedBtn!: HTMLButtonElement;
  private savingsPill!: HTMLElement;
  private slashChipsEl!: HTMLElement;
  private sendBtn!: HTMLButtonElement;
  private modesEl!: HTMLElement;

  constructor(container: HTMLElement, initialModel: string, callbacks: ChatCallbacks) {
    this.callbacks = callbacks;

    const header = document.createElement('div');
    header.className = 'pane-header chat-header';

    const title = document.createElement('div');
    title.className = 'chat-title';
    title.innerHTML = '<span class="pane-title">Chat</span>';
    this.savingsPill = document.createElement('span');
    this.savingsPill.className = 'savings-pill';
    this.savingsPill.hidden = true;
    title.appendChild(this.savingsPill);
    header.appendChild(title);

    const conversationControls = document.createElement('div');
    conversationControls.className = 'conversation-controls';

    this.modelSelect = document.createElement('select');
    this.modelSelect.className = 'model-select';
    this.modelSelect.title = 'Model used for this chat';
    for (const model of AVAILABLE_MODELS) {
      const opt = document.createElement('option');
      opt.value = model.id;
      opt.textContent = model.label;
      opt.selected = model.id === initialModel;
      this.modelSelect.appendChild(opt);
    }
    this.modelSelect.addEventListener('change', () => this.callbacks.onModelChange(this.modelSelect.value));
    conversationControls.appendChild(this.modelSelect);

    const overviewBtn = document.createElement('button');
    overviewBtn.className = 'btn subtle';
    overviewBtn.textContent = 'Overview';
    overviewBtn.title = "The active project's roadmap, director context, and per-agent memory (read-only)";
    overviewBtn.addEventListener('click', () => void showContextTreesOverview());
    conversationControls.appendChild(overviewBtn);

    this.conversationSelect = document.createElement('select');
    this.conversationSelect.className = 'conversation-select';
    this.conversationSelect.addEventListener('change', () => {
      const id = this.conversationSelect.value;
      if (id) this.callbacks.onSelectConversation(id);
    });
    conversationControls.appendChild(this.conversationSelect);

    const newBtn = document.createElement('button');
    newBtn.className = 'btn subtle';
    newBtn.textContent = 'New';
    newBtn.title = 'Start a new conversation';
    newBtn.addEventListener('click', () => this.callbacks.onNewConversation());
    conversationControls.appendChild(newBtn);

    header.appendChild(conversationControls);
    container.appendChild(header);

    this.messagesEl = document.createElement('div');
    this.messagesEl.id = 'chat-messages';
    container.appendChild(this.messagesEl);
    this.renderEmptyState();

    const modes = document.createElement('div');
    modes.id = 'chat-modes';
    modes.dataset.mode = 'chat';
    this.modesEl = modes;

    const comparisonBar = document.createElement('div');
    comparisonBar.id = 'comparison-bar';
    const comparisonLabel = document.createElement('label');
    this.comparisonToggle = document.createElement('input');
    this.comparisonToggle.type = 'checkbox';
    comparisonLabel.appendChild(this.comparisonToggle);
    comparisonLabel.append(' Benchmark comparison mode');
    comparisonLabel.title =
      "While this is on, each run uploads your prompt, the context blocks sent and Claude's reply — encrypted — to the project's servers so the pipeline's real-world effect can be analyzed. Turn it off and only anonymous metadata is sent.";
    comparisonBar.appendChild(comparisonLabel);

    this.comparisonMaxTokensInput = document.createElement('input');
    this.comparisonMaxTokensInput.type = 'number';
    this.comparisonMaxTokensInput.min = '500';
    this.comparisonMaxTokensInput.max = '50000';
    this.comparisonMaxTokensInput.value = '4000';
    this.comparisonMaxTokensInput.title =
      'Upper bound on the naive/unoptimized baseline\'s context — it\'s truncated to match whatever your optimized settings actually use, capped at this value';
    this.comparisonMaxTokensInput.style.display = 'none';
    comparisonBar.appendChild(this.comparisonMaxTokensInput);

    const historyBtn = document.createElement('button');
    historyBtn.className = 'btn subtle';
    historyBtn.textContent = 'History';
    historyBtn.title = 'Browse previously-saved benchmark comparison runs';
    historyBtn.addEventListener('click', () => this.callbacks.onShowBenchmarkHistory());
    comparisonBar.appendChild(historyBtn);

    modes.appendChild(comparisonBar);

    const agentBar = document.createElement('div');
    agentBar.id = 'agent-bar';
    const agentLabel = document.createElement('label');
    this.agentToggle = document.createElement('input');
    this.agentToggle.type = 'checkbox';
    agentLabel.appendChild(this.agentToggle);
    agentLabel.append(' Agent mode');
    agentBar.appendChild(agentLabel);

    this.agentPersonaSelect = document.createElement('select');
    this.agentPersonaSelect.className = 'agent-persona-select';
    for (const persona of AGENT_PERSONAS) {
      const opt = document.createElement('option');
      opt.value = persona.id;
      opt.textContent = persona.label;
      opt.title = persona.description;
      this.agentPersonaSelect.appendChild(opt);
    }
    this.agentPersonaSelect.title = AGENT_PERSONAS[0].description;
    this.agentPersonaSelect.addEventListener('change', () => {
      const persona = AGENT_PERSONAS.find((p) => p.id === this.agentPersonaSelect.value);
      if (persona) this.agentPersonaSelect.title = persona.description;
    });
    this.agentPersonaSelect.style.display = 'none';
    agentBar.appendChild(this.agentPersonaSelect);
    modes.appendChild(agentBar);

    // Tier-2 telemetry disclosure. Always visible (not only once the toggle is on) so it can inform
    // the decision to turn it on; it brightens from muted to full-contrast while the mode is active.
    const consentNote = document.createElement('span');
    consentNote.className = 'mode-consent-note';
    consentNote.textContent =
      "Benchmark comparison mode sends your prompt, the code context and Claude's reply — encrypted — to the project's servers for analysis. Other sends report metadata only.";
    modes.appendChild(consentNote);

    container.appendChild(modes);

    // Comparison mode and agent mode are mutually exclusive sends, and neither makes sense
    // alongside "Send unoptimized" (comparison inherently runs an unoptimized baseline itself;
    // the agent loop always uses the optimized pipeline for its context).
    this.comparisonToggle.addEventListener('change', () => {
      const on = this.comparisonToggle.checked;
      this.comparisonMaxTokensInput.style.display = on ? '' : 'none';
      if (on) {
        this.agentToggle.checked = false;
        this.agentPersonaSelect.style.display = 'none';
      }
      this.unoptimizedBtn.disabled = on || this.agentToggle.checked;
      this.syncModeState();
    });
    this.agentToggle.addEventListener('change', () => {
      const on = this.agentToggle.checked;
      this.agentPersonaSelect.style.display = on ? '' : 'none';
      if (on) {
        this.comparisonToggle.checked = false;
        this.comparisonMaxTokensInput.style.display = 'none';
      }
      this.unoptimizedBtn.disabled = on || this.comparisonToggle.checked;
      this.syncModeState();
    });

    const composer = document.createElement('div');
    composer.id = 'chat-composer';

    this.slashChipsEl = document.createElement('div');
    this.slashChipsEl.id = 'slash-chips';
    composer.appendChild(this.slashChipsEl);

    const inputBar = document.createElement('div');
    inputBar.id = 'chat-input-bar';
    this.inputEl = document.createElement('textarea');
    this.inputEl.rows = 1;
    this.inputEl.placeholder = 'Ask about your code…';
    this.inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.send(true);
      }
    });
    this.inputEl.addEventListener('input', () => this.autoGrow());
    inputBar.appendChild(this.inputEl);

    const btnCol = document.createElement('div');
    btnCol.className = 'btn-col';
    const sendBtn = document.createElement('button');
    sendBtn.className = 'btn primary send-btn';
    sendBtn.textContent = 'Send';
    sendBtn.addEventListener('click', () => this.send(true));
    this.sendBtn = sendBtn;
    this.unoptimizedBtn = document.createElement('button');
    this.unoptimizedBtn.className = 'btn subtle';
    this.unoptimizedBtn.textContent = 'Send unoptimized';
    this.unoptimizedBtn.title = 'Bypass every optimization stage for this one message';
    this.unoptimizedBtn.addEventListener('click', () => this.send(false));
    btnCol.appendChild(sendBtn);
    btnCol.appendChild(this.unoptimizedBtn);
    inputBar.appendChild(btnCol);

    composer.appendChild(inputBar);
    container.appendChild(composer);

    void this.loadSlashChips();
    document.addEventListener(TEMPLATES_CHANGED_EVENT, () => void this.loadSlashChips());
  }

  /** Chips for the E3 task templates the user actually has configured — not a hardcoded list. */
  private async loadSlashChips() {
    const templates = await getTaskTemplates();
    this.slashChipsEl.innerHTML = '';
    const commands = Object.keys(templates);
    this.slashChipsEl.hidden = commands.length === 0;
    for (const command of commands) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'slash-chip mono';
      chip.textContent = command;
      chip.title = templates[command];
      chip.addEventListener('click', () => this.applySlashCommand(command));
      this.slashChipsEl.appendChild(chip);
    }
  }

  private applySlashCommand(command: string) {
    const rest = this.inputEl.value.replace(/^\/\S*\s*/, '');
    this.inputEl.value = `${command} ${rest}`;
    this.inputEl.focus();
    this.inputEl.setSelectionRange(this.inputEl.value.length, this.inputEl.value.length);
    this.autoGrow();
  }

  /** The send button does three different things — say which one it's about to do. */
  private syncModeState() {
    const mode = this.comparisonToggle.checked ? 'comparison' : this.agentToggle.checked ? 'agent' : 'chat';
    this.modesEl.dataset.mode = mode;
    this.sendBtn.textContent = mode === 'comparison' ? 'Compare' : mode === 'agent' ? 'Run agent' : 'Send';
  }

  private autoGrow() {
    this.inputEl.style.height = 'auto';
    this.inputEl.style.height = `${Math.min(this.inputEl.scrollHeight, 180)}px`;
  }

  private send(optimized: boolean) {
    const text = this.inputEl.value.trim();
    if (!text) return;
    this.inputEl.value = '';
    this.autoGrow();
    if (this.comparisonToggle.checked) {
      const maxTokens = Number(this.comparisonMaxTokensInput.value) || 4000;
      this.callbacks.onRunComparison(text, maxTokens);
      return;
    }
    if (this.agentToggle.checked) {
      this.callbacks.onRunAgent(text, this.agentPersonaSelect.value);
      return;
    }
    this.callbacks.onSend(text, optimized);
  }

  /**
   * Session savings shown as a pill next to the chat title. Stays hidden until there's a real
   * request to compare against — no invented percentage on a cold start.
   */
  setSavings(baselineTokens: number, sentTokens: number) {
    if (baselineTokens <= 0) {
      this.savingsPill.hidden = true;
      return;
    }
    const saved = Math.max(0, baselineTokens - sentTokens);
    const pct = (saved / baselineTokens) * 100;
    this.savingsPill.hidden = false;
    this.savingsPill.textContent = `${pct.toFixed(0)}% lean`;
    this.savingsPill.title = `This session: sent ~${sentTokens.toLocaleString()} tokens instead of ~${baselineTokens.toLocaleString()} — ${saved.toLocaleString()} saved (estimated).`;
  }

  private renderEmptyState() {
    const empty = document.createElement('div');
    empty.className = 'chat-empty';
    const mark = document.createElement('img');
    mark.className = 'chat-empty-mark';
    mark.src = '/icons/icon.svg';
    mark.alt = '';
    empty.appendChild(mark);
    const body = document.createElement('div');
    body.innerHTML =
      '<div class="chat-empty-title">Ask Naulon about your code</div>' +
      '<div class="chat-empty-body">Every message runs through the optimization pipeline first — open files are trimmed, ranked and de-duplicated before anything is sent.</div>';
    empty.appendChild(body);
    this.messagesEl.appendChild(empty);
  }

  private clearEmptyState() {
    this.messagesEl.querySelector('.chat-empty')?.remove();
  }

  renderHistory(turns: ConversationTurn[]) {
    this.messagesEl.innerHTML = '';
    for (const turn of turns) this.appendMessage(turn.role, turn.content);
    if (turns.length === 0) this.renderEmptyState();
  }

  setConversationList(conversations: ConversationListItem[], activeId: string) {
    const sorted = [...conversations].sort((a, b) => b.updatedAt - a.updatedAt);
    this.conversationSelect.innerHTML = '';
    if (!sorted.some((c) => c.id === activeId)) {
      const current = document.createElement('option');
      current.value = activeId;
      current.textContent = '(new, unsaved conversation)';
      this.conversationSelect.appendChild(current);
    }
    for (const c of sorted) {
      const option = document.createElement('option');
      option.value = c.id;
      option.textContent = c.title;
      option.selected = c.id === activeId;
      this.conversationSelect.appendChild(option);
    }
  }

  appendMessage(role: 'user' | 'assistant', content: string) {
    this.clearEmptyState();
    const el = document.createElement('div');
    el.className = `msg ${role}`;
    this.renderContent(el, content);
    this.messagesEl.appendChild(el);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    return el;
  }

  /** Shows a tool-call/progress line in the transcript, distinct from a user/assistant bubble. */
  appendAgentStatus(text: string): void {
    this.clearEmptyState();
    const el = document.createElement('div');
    el.className = 'msg agent-status';
    el.textContent = text;
    this.messagesEl.appendChild(el);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  beginStreamingAssistantMessage(): void {
    this.streamingText = '';
    this.streamingEl = this.appendMessage('assistant', '');
    this.streamingEl.classList.add('streaming');
  }

  appendStreamingDelta(delta: string): void {
    if (!this.streamingEl) return;
    this.streamingText += delta;
    this.renderContent(this.streamingEl, this.streamingText);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  endStreamingAssistantMessage(): string {
    const final = this.streamingText;
    this.streamingEl?.classList.remove('streaming');
    this.streamingEl = null;
    this.streamingText = '';
    return final;
  }

  private renderContent(el: HTMLElement, markdown: string) {
    el.innerHTML = marked.parse(markdown, { async: false }) as string;
    el.querySelectorAll('pre code').forEach((block) => {
      hljs.highlightElement(block as HTMLElement);
      const pre = block.parentElement;
      if (!pre || pre.querySelector('.apply-diff-btn')) return;
      const btn = document.createElement('button');
      btn.className = 'apply-diff-btn';
      btn.textContent = 'Apply to open file';
      btn.addEventListener('click', () => this.callbacks.onApplyCodeBlock(block.textContent ?? ''));
      pre.appendChild(btn);
    });
  }
}
